import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import ListCreationScreen from '@alltrails/amplitude/enums/ListCreationScreen';
import ListItemType from '@alltrails/amplitude/enums/ListItemType';
import MapLocation from '@alltrails/amplitude/enums/MapLocation';
import MapType from '@alltrails/amplitude/enums/MapType';
import NavigatedFrom from '@alltrails/amplitude/enums/NavigatedFrom';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logCreateNewListClicked from '@alltrails/amplitude/events/logCreateNewListClicked';
import logListAdded from '@alltrails/amplitude/events/logListAdded';
import logListItemAdded from '@alltrails/amplitude/events/logListItemAdded';
import logSaveClicked from '@alltrails/amplitude/events/logSaveClicked';
import logSaveListSelected from '@alltrails/amplitude/events/logSaveListSelected';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import { completedListId, favoritesListId } from './listUtils';

export type RiverAnalyticsData = {
  riverIndex: number;
  filters?: string;
  riverSectionType?: string;
  riverTitle?: string;
  riverTitleFilter?: string[];
};

export type AnalyticsInfo = {
  page: string;
  trailIndex?: number;
  detailedCardLocation?: CardLocation;
  trailPageType?: TrailPageType;
  listType?: string;
  navigatedFrom?: NavigatedFrom;
};

export type ListItemAddedAnalyticsData = { list_id: number; list_item_type: MapType; list_type: ListItemType; map_id: number; map_type: MapType };

const PageToAmplitudeMappings: Record<string, { listCreationScreen: ListCreationScreen; mapLocation?: MapLocation }> = {
  [PageStrings.EXPLORE_ALL_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'explore'
  },
  [PageStrings.EXPLORE_COMMUNITY_CONTENT_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'explore'
  },
  [PageStrings.EXPLORE_USERS_MAPS_MAP_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'saved map'
  },
  [PageStrings.EXPLORE_USERS_MAPS_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'saved map'
  },
  [PageStrings.LISTS_PAGE]: {
    listCreationScreen: 'saved tab'
  },
  [PageStrings.TRAIL_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'trail'
  },
  [PageStrings.EXPLORE_TRAIL_MAP_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'trail'
  },
  [PageStrings.EXPLORE_COMPLETED_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'activity'
  },
  [PageStrings.EXPLORE_USERS_TRACKS_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'activity'
  },
  [PageStrings.EXPLORE_USERS_TRACKS_MAP_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'activity'
  },
  [PageStrings.HOMEPAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'web homepage'
  },
  [PageStrings.SEO_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'location page'
  },
  [PageStrings.SPONSORED_LIST]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'inspirational list page'
  },
  [PageStrings.TRIP_PLANNER_LIST_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'trip planner'
  },
  [PageStrings.TRIP_PLANNER_MAP_PAGE]: {
    listCreationScreen: 'saved item flow',
    mapLocation: 'trip planner map'
  }
};

const listIdToAlgoliaListItemType = (listId: number, page?: string): ListItemType => {
  if (listId === favoritesListId) {
    return 'favorites';
  }
  if (listId === completedListId) {
    return 'completed';
  }
  if (page === PageStrings.SPONSORED_LIST) {
    return 'sponsored list';
  }
  return 'custom list';
};

const algoliaTypeToAmplitudeMapType = (type: string): MapType => {
  if (type === 'track') {
    return 'activity';
  }
  if (type === 'trail') {
    return 'trail';
  }
  return 'map';
};

export const logSaveClickedHelper = (itemId: number, type: string, analyticsInfo: AnalyticsInfo) => {
  const { page, detailedCardLocation, trailPageType, listType, navigatedFrom } = analyticsInfo;
  const mapLocation = PageToAmplitudeMappings[page]?.mapLocation as MapLocation;

  logSaveClicked({
    detailed_card_location: detailedCardLocation,
    inspirational_list_type: listType,
    map_id: itemId,
    map_type: algoliaTypeToAmplitudeMapType(type),
    navigated_from: navigatedFrom,
    save_location: mapLocation,
    trail_page_type: trailPageType
  });
};

export const logListAddedHelper = (page: string, listId: number) => {
  const listCreationScreen = PageToAmplitudeMappings[page]?.listCreationScreen;

  if (listCreationScreen) {
    logListAdded({ list_creation_screen: listCreationScreen, list_id: listId, list_type: listIdToAlgoliaListItemType(listId) });
  }
};

export const logCreateNewListClickedHelper = (page: string) => {
  const listCreationScreen = PageToAmplitudeMappings[page]?.listCreationScreen;

  if (listCreationScreen) {
    logCreateNewListClicked({ list_creation_screen: listCreationScreen });
  }
};

export const logSaveListSelectedHelper = (listId: number, itemId: number, type: string, page?: string) => {
  logSaveListSelected({
    list_id: listId,
    list_type: listIdToAlgoliaListItemType(listId, page),
    map_id: itemId,
    map_type: algoliaTypeToAmplitudeMapType(type)
  });
};

export const logListItemAddedHelper = (listId: number, itemId: number, type: string, page?: string) => {
  logListItemAdded({
    list_id: listId,
    list_type: listIdToAlgoliaListItemType(listId, page),
    map_id: itemId,
    map_type: algoliaTypeToAmplitudeMapType(type),
    list_item_type: algoliaTypeToAmplitudeMapType(type)
  });
};
